import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './GameLayout.module.css';
import PropTypes from 'prop-types';
import { GAME_THEME } from '../../constants/GameTheme';
import { useLowReady } from '../../context/LowReadyContext';

const LOW_READY_PENALTY = 0.6;

// ── Low Ready Aim Box ─────────────────────────────────────────────────────────
const LowReadyAimBox = () => (
    <div style={{
        position: 'absolute',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90px',
        height: '90px',
        border: '2px dashed rgba(255,255,255,0.5)',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.05)',
        pointerEvents: 'none',
        zIndex: 5,
        gap: '4px',
    }}>
        {/* Corner brackets */}
        {[
            { top: 4, left: 4, borderTop: '2px solid rgba(255,255,255,0.6)', borderLeft: '2px solid rgba(255,255,255,0.6)' },
            { top: 4, right: 4, borderTop: '2px solid rgba(255,255,255,0.6)', borderRight: '2px solid rgba(255,255,255,0.6)' },
            { bottom: 4, left: 4, borderBottom: '2px solid rgba(255,255,255,0.6)', borderLeft: '2px solid rgba(255,255,255,0.6)' },
            { bottom: 4, right: 4, borderBottom: '2px solid rgba(255,255,255,0.6)', borderRight: '2px solid rgba(255,255,255,0.6)' },
        ].map((s, i) => (
            <div key={i} style={{ position: 'absolute', width: '12px', height: '12px', ...s }} />
        ))}
        <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold', letterSpacing: '0.5px', textAlign: 'center', lineHeight: 1.3 }}>
            AIM HERE
        </div>
        <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.45)', textAlign: 'center', lineHeight: 1.3 }}>
            LOW READY
        </div>
        {/* Crosshair */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '16px', height: '16px' }}>
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: 'rgba(255,255,255,0.3)' }} />
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.3)' }} />
        </div>
    </div>
);

// ── Low Ready Sidebar Toggle ──────────────────────────────────────────────────
const LowReadyToggle = ({ lowReady, setLowReady }) => (
    <div style={{
        background: lowReady ? '#1a0a0a' : '#111',
        border: `1px solid ${lowReady ? '#e74c3c88' : '#333'}`,
        borderRadius: '6px',
        padding: '10px 12px',
        marginBottom: '12px',
        transition: 'all 0.2s',
    }}>
        <label style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            gap: '8px',
        }}>
            <div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: lowReady ? '#e74c3c' : '#aaa' }}>
                    Low Ready
                </div>
                <div style={{ fontSize: '10px', color: '#555', marginTop: '2px' }}>
                    -{LOW_READY_PENALTY}s par time
                </div>
            </div>
            {/* Toggle switch */}
            <div
                onClick={() => setLowReady(v => !v)}
                style={{
                    width: '38px',
                    height: '20px',
                    borderRadius: '10px',
                    background: lowReady ? '#e74c3c' : '#333',
                    position: 'relative',
                    transition: 'background 0.2s',
                    flexShrink: 0,
                }}
            >
                <div style={{
                    position: 'absolute',
                    top: '3px',
                    left: lowReady ? '21px' : '3px',
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }} />
            </div>
        </label>
    </div>
);

LowReadyToggle.propTypes = {
    lowReady: PropTypes.bool.isRequired,
    setLowReady: PropTypes.func.isRequired,
};

// ── Main GameLayout ───────────────────────────────────────────────────────────
const GameLayout = ({
    sidebarContent,
    children,
    onCanvasClick,
    canvasRef,
    gameRunning = false,
    onQuit,
    infoButton
}) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
    const { lowReady, setLowReady } = useLowReady();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 800);

            if (canvasRef && canvasRef.current && canvasRef.current.parentElement) {
                const canvas = canvasRef.current;
                const parent = canvas.parentElement;
                canvas.width = parent.clientWidth;
                canvas.height = parent.clientHeight;
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();
        const timeout = setTimeout(handleResize, 10);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeout);
        };
    }, [canvasRef, gameRunning]);

    const showSidebar = !isMobile || !gameRunning;

    return (
        <div
            className={styles.container}
            style={{
                '--btn-bg': GAME_THEME.BUTTON_BG,
                '--btn-color': GAME_THEME.BUTTON_COLOR,
                '--btn-hover-bg': GAME_THEME.BUTTON_HOVER_BG,
                '--quit-bg': GAME_THEME.QUIT_BUTTON_BG,
                '--quit-hover-bg': GAME_THEME.QUIT_BUTTON_HOVER_BG
            }}
        >
            <div className={`${styles.sidebar} ${!showSidebar ? styles.sidebarHidden : ''}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <button
                        onClick={() => navigate('/games')}
                        style={{
                            flex: 1,
                            padding: '10px 20px',
                            background: GAME_THEME.BUTTON_BG,
                            color: GAME_THEME.BUTTON_COLOR,
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        ← GAMES
                    </button>
                    {infoButton && infoButton}
                </div>

                {/* Low Ready Toggle */}
                <LowReadyToggle lowReady={lowReady} setLowReady={setLowReady} />

                {sidebarContent}
            </div>

            <div className={styles.gameArea}>
                {!showSidebar && onQuit && (
                    <div className={styles.topBar}>
                        <button className={styles.quitButton} onClick={onQuit}>EXIT GAME</button>
                    </div>
                )}

                <div className={styles.contentWrapper}>
                    {canvasRef && (
                        <canvas
                            ref={canvasRef}
                            className={styles.canvas}
                            onClick={onCanvasClick}
                        />
                    )}
                    {children}
                    {/* Low Ready Aim Box */}
                    {lowReady && <LowReadyAimBox />}
                </div>
            </div>
        </div>
    );
};

GameLayout.propTypes = {
    sidebarContent: PropTypes.node,
    children: PropTypes.node,
    onCanvasClick: PropTypes.func,
    gameRunning: PropTypes.bool,
    onQuit: PropTypes.func,
    infoButton: PropTypes.node,
    canvasRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ])
};

export default GameLayout;
